import type { ThunkAction, AppState } from '../../types/app.types';
import type { V2Response } from '../../types/api-v2.types';
import type { Drugs } from './drugs.types';
import { trackEvent } from '../../utils/analytics';
import { getUserPharmacyLocationId } from './pharmacy-module';
import type { Drug } from './drugs.types';
import { getAccountSummary } from './account-module';
import trackLinkSharePurchase from '../../utils/linkshare-purchase';
import { ENABLE_TELEHEALTH_SELF_SERVICE_PAUSE } from '../../config';
import STRIPE_STATUS from '../../utils/stripe-statuses';
import { AnyAction } from 'redux';
import { getSchedules } from './recurring-schedule-module';

/* ------------------ state ------------------ */
export type Subscription = {
  id: string;
  createdOn: string;
  nextChargeOn: string;
  cycleCostCents: number;
  planId: string;
  planName: string;
  consultationLink: string | null | undefined;
  consultationStatus: string | null | undefined;
  cycleLength: number;
  conditionId?: string;
  conditionName?: string;
  nextAutoRefillDate?: string;
  medName?: string;
  medId?: string;
  isPaused: boolean;
  stripeStatus?: string;
  cancelledOn?: string;
};
export type SubscriptionLimitation = {
  conditionId: string;
  conditionName: string;
  consultationLink: string;
  refillConsultEligibility: string;
  hasSub: boolean;
  medNameId: string;
};
export type Coupon = {
  appliedAmount: string;
  description: string;
  rawAmount: number;
};
export type Med = {
  formattedDescription: string;
  formattedName: string;
  conditionId: string | null | undefined;
  medId: string;
  medNameId: string;
  medPriceId: string;
  price: number;
  quantity: number;
  isTelemed: boolean;
};
export type SubsCart = {
  coupon: Coupon;
  med: Med;
  medicalFee: number;
  subscriptionFee: number;
  totalPrice: string;
  totalPriceWithCoupons: number;
  isEligible: boolean | null | undefined;
};
type SubAddedProduct = {
  medId: string;
  medNameId: string;
  quantity: number | null | undefined;
  medPriceId: string | null | undefined;
};
export type ConsultActionItems = {
  consultationLink: string;
  medName: string;
  condition: string;
  status: string;
};
export type SubsActionItems = {
  consultActionItems: Array<ConsultActionItems>;
};
export type SubscriptionsState = {
  subscriptions: Array<Subscription>;
  cart: SubsCart | null | undefined;
  subsEligibility: {
    medNameId?: boolean;
  };
  subscriptionLimitations: {
    medNameId?: SubscriptionLimitation;
  };
  subsActionItems: SubsActionItems | null | undefined;
  selectedMDVMed?: Drug;
};

interface SubscriptionDetailsProps {
  name?: string;
  firstSubDate?: string;
  nextChargeDate?: string;
  subCost?: string;
  nextAutoRefillDate?: string;
  isCancelled?: boolean;
  cancelledDate?: string;
}

/* ------------------ constants ------------------ */
export const POPULATE_SUBSCRIPTION = 'rx/subscriptions/POPULATE_SUBSCRIPTIONS';
export const POPULATE_SUBSCRIPTION_LIMITATION = 'rx/subscriptions/POPULATE_SUBSCRIPTIONS_ELIGIBILITY';
export const GET_CART_INFO = 'rx/subscriptions/GET_CART_INFO';
export const SET_SUBS_ELIGIBILITY = 'rx/subscriptions/SET_SUBS_ELIGIBILITY';
export const PURCHASE = 'rx/subscriptions/PURCHASE';
export const GET_SUBS_ACTION_ITEMS = 'rx/subscriptions/GET_SUBS_ACTION_ITEMS';
export const USER_SELECTED_MED = 'rx/subscriptions/USER_SELECTED_MED';

type PopulateSubscriptions = {
  type: 'rx/subscriptions/POPULATE_SUBSCRIPTIONS';
  payload: Subscription;
};
type PopulateCart = {
  type: 'rx/subscriptions/GET_CART_INFO';
  payload: SubsCart;
};
type SetSubsEligibility = {
  type: 'rx/subscriptions/SET_SUBS_ELIGIBILITY';
  payload: any;
};
type PopulateSubscriptionsLimitations = {
  type: 'rx/subscriptions/POPULATE_SUBSCRIPTIONS_ELIGIBILITY';
  payload: SubscriptionLimitation;
};
type Purchase = {
  type: 'rx/subscriptions/PURCHASE';
};
type SaveMedSelection = {
  type: 'rx/subscriptions/USER_SELECTED_MED';
  payload: Drug;
};
type SetSubsActionItems = {
  type: 'rx/subscriptions/GET_SUBS_ACTION_ITEMS';
  payload: SubsActionItems;
};

export type SubscriptionAction =
  | PopulateSubscriptions
  | PopulateCart
  | SetSubsEligibility
  | PopulateSubscriptionsLimitations
  | Purchase
  | SetSubsActionItems
  | SaveMedSelection;

/* ------------------ action creators ------------------ */
export function getSubscriptions(): ThunkAction<Promise<any>> {
  return (dispatch, getState, { blinkAPIClient }): Promise<any> => {
    const api = ENABLE_TELEHEALTH_SELF_SERVICE_PAUSE ? '/v2/subscription?statuses=ALL' : '/v2/subscription';
    return blinkAPIClient
      .get(api)
      .then((res) => res.data)
      .then((body) => {
        body.result && dispatch(populateSubscriptions(body.result));
      })
      .catch((err) => console.error(err));
  };
}

export function getSubsEligibility(): ThunkAction<Promise<any>> {
  return (dispatch, getState, { blinkAPIClient }): Promise<any> => {
    const state = getState();
    const med = state && state.subscriptions && state.subscriptions.cart && state.subscriptions.cart.med;
    const medNameId = (med && med.medNameId) || '';
    const medId = (med && med.medId) || '';
    const req = `/v2/subscription/eligibility?med_name_id=${medNameId}&med_id=${medId}`;
    return blinkAPIClient
      .get(req)
      .then((res) => res.data)
      .then((body) => {
        body.result && dispatch(populateSubscriptionsLimitations(body.result));
      });
  };
}

// Used to get payload for SubsCart creation from redux-store.drugs
export function getSubsCartCreationPayload(drugs: Drugs, medSlug: string): SubAddedProduct {
  const drug = drugs[medSlug];
  const { selectedFormIndex: find, selectedDosageIndex: dind, selectedQuantityIndex: qind } = drug;
  const dosage = drug.forms[find].dosages[dind];
  const quantities = dosage.quantities;
  const selection = quantities && quantities[qind];
  const medId = drug.forms[find].dosages[dind].medId;
  const medNameId = drug.medNameId;
  return {
    medId: medId,
    medNameId: medNameId,
    medPriceId: selection && selection.price && selection.price.delivery && selection.price.delivery.priceId,
    quantity: selection && selection.rawQuantity,
  };
}

export function createSubsCart(payload: SubAddedProduct): ThunkAction<Promise<any>> {
  return (dispatch, getState, { blinkAPIClient }: any): Promise<any> => {
    return blinkAPIClient
      .post(`/v2/subscription/cart`, payload)
      .then((res) => res.data)
      .then((data: V2Response<SubsCart>) => {
        dispatch(
          trackEvent('Added Product', {
            ...payload,
            pharmacyNetworkId: 3,
            isSubs: true,
            priceId: payload['medPriceId'],
          })
        );
        dispatch(populateCart(data.result));
      })
      .catch((err) => console.error(err));
  };
}

export function getSubsCart(): ThunkAction<Promise<any>> {
  return (dispatch, getState, { blinkAPIClient }: any): Promise<any> => {
    return blinkAPIClient
      .get(`/v2/subscription/cart`)
      .then((res) => res.data)
      .then((data: V2Response<SubsCart>) => {
        dispatch(populateCart(data.result));
      })
      .catch((err) => console.error(err));
  };
}

export function getSubsActionItems(): ThunkAction<Promise<any>> {
  return (dispatch, _, { blinkAPIClient }: any): Promise<any> => {
    return blinkAPIClient
      .get(`/v2/subs_action_items`)
      .then((res) => res.data)
      .then((data: V2Response<SubsActionItems>) => {
        dispatch(setSubsActionItems(data.result));
      })
      .catch((err) => console.error(err));
  };
}

export function pauseSubscription(subscriptionId: string, duration: number): ThunkAction<Promise<any>> {
  return (dispatch, _, { blinkAPIClient }: any): Promise<any> => {
    return blinkAPIClient
      .post(`/v2/subscription/pause/${subscriptionId}/duration/${duration}`)
      .then((res) => res.data)
      .then((data) => {
        dispatch(getSubscriptions());
        return data;
      })
      .catch((err) => err);
  };
}

export function unPauseSubscription(subscriptionId: string): ThunkAction<Promise<any>> {
  return (dispatch, _, { blinkAPIClient }: any): Promise<any> => {
    return blinkAPIClient
      .post(`/v2/subscription/unpause/${subscriptionId}`)
      .then((res) => res.data)
      .then((data) => {
        dispatch(getSubscriptions());
        return data;
      })
      .catch((err) => err);
  };
}

export function cancelSubscription(subscriptionId: string): ThunkAction<Promise<any>> {
  return (dispatch, _, { blinkAPIClient }: any): Promise<any> => {
    return blinkAPIClient
      .delete(`/v2/subscription/${subscriptionId}`)
      .then((res) => res.data)
      .then((data) => {
        dispatch(getSubscriptions());
        return data;
      })
      .catch((err) => err);
  };
}

export function persistMedSelection(med: Drug): ThunkAction<Promise<any>> {
  return (dispatch): Promise<any> => {
    return Promise.resolve(dispatch(saveMedSelection(med)));
  };
}

export const getPlanDetails = (sub: Subscription): SubscriptionDetailsProps => {
  const { createdOn, nextChargeOn, nextAutoRefillDate = '', stripeStatus, cancelledOn = '' } = sub;
  const created = new Date(createdOn);
  const nextChargeDate = new Date(nextChargeOn);
  const nextRefillDate = new Date(nextAutoRefillDate);
  const cancelledDate = new Date(cancelledOn);
  return {
    name: formatSubName(sub.planName, true) + ' Subscription',
    firstSubDate: dateToString(created),
    nextChargeDate: dateToString(nextChargeDate),
    subCost: formatAmountFromCents(sub.cycleCostCents),
    nextAutoRefillDate: nextAutoRefillDate ? dateToString(nextRefillDate) : '',
    isCancelled: stripeStatus === STRIPE_STATUS.CANCELLED,
    cancelledDate: cancelledOn ? dateToString(cancelledDate) : '',
  };
};

export function dateToString(date: Date) {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = date.getUTCFullYear();
  return `${month} ${day}, ${year}`;
}

export function formatAmountFromCents(amount: number) {
  const cost = amount.toString();
  const cents = cost.substr(cost.length - 2);
  const dollars = cost.substr(0, cost.length - 2);
  return `$${dollars}.${cents}`;
}

export function formatSubName(name: string, capitalize?: boolean) {
  let _name = name.toLowerCase();

  if (_name.indexOf('trial') > -1) {
    _name = _name.substr(0, name.length - 5);
  }

  _name = _name.replace(/_/g, ' ').trim();

  if (capitalize) {
    _name = _name.replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()));
  }

  return _name;
}

export function populateSubscriptions(subs: Subscription): PopulateSubscriptions {
  return {
    type: POPULATE_SUBSCRIPTION,
    payload: subs,
  };
}

export function populateCart(subsCart: SubsCart): PopulateCart {
  return {
    type: GET_CART_INFO,
    payload: subsCart,
  };
}

export function populateSubscriptionsLimitations(
  subsLimitations: SubscriptionLimitation
): PopulateSubscriptionsLimitations {
  return {
    type: POPULATE_SUBSCRIPTION_LIMITATION,
    payload: subsLimitations,
  };
}

export function setSubsEligibility(medNameId: string, isEligible: boolean): AnyAction {
  return {
    type: SET_SUBS_ELIGIBILITY,
    payload: {
      [medNameId]: isEligible,
    },
  };
}

function setSubsActionItems(subsActionItems: SubsActionItems): SetSubsActionItems {
  return {
    type: GET_SUBS_ACTION_ITEMS,
    payload: subsActionItems,
  };
}

export const isEligible = (state: SubscriptionsState) => {
  return state.cart && state.cart.med && state.subsEligibility[state.cart.med.medNameId];
};

export function purchaseSub(order: {
  medId: string;
  quantity: number;
  medNameId: string;
  medPriceId: string;
  sanofiConsentGiven: boolean;
}): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const { account } = getState();
    const telemedCurrentState = account.telemedCurrentState;
    return blinkAPIClient
      .post('/v2/subscription/drug_purchase', { ...order, sanofiConsentGiven: false, telemedCurrentState })
      .then((res) => res.data)
      .then(async (data) => {
        await Promise.all([dispatch(getSubscriptions()), dispatch(getSchedules())]);
        const state: AppState = getState();
        const orderCount = state.account.orderCount;
        const subOrderCount = state.subscriptions && state.subscriptions.subscriptions.length;
        const selectedPharmacyId = getUserPharmacyLocationId(state.pharmacy.selectedPharmacy);
        const isTelemed =
          state.subscriptions.cart && state.subscriptions.cart.med && state.subscriptions.cart.med.isTelemed;
        dispatch(
          trackEvent('Completed Order', {
            ...data.result,
            orderCount,
            subOrderCount: subOrderCount - 1,
            pharmacyId: selectedPharmacyId,
            isSubs: true,
            isTelemed: isTelemed,
          })
        );
        // Linkshare tracking for affiliate events
        trackLinkSharePurchase(data.result.orderId, dispatch);
        dispatch(getAccountSummary());
        dispatch({
          type: PURCHASE,
        });
        return data;
      });
  };
}

export function saveMedSelection(selectedMed: Drug): SaveMedSelection {
  return {
    type: USER_SELECTED_MED,
    payload: selectedMed,
  };
}

/* ------------------ selectors ------------------ */
export function getSubscriptionsCartFee(state: any) {
  return state.subscriptions && state.subscriptions.cart && state.subscriptions.cart.subscriptionFee;
}

export function getSubscriptionsCartMed(state: any) {
  const med = state.subscriptions && state.subscriptions.cart && state.subscriptions.cart.med;
  return med || {};
}

export function getIsSubscriptionsEligible(state: any) {
  return isEligible(state.subscriptions);
}

export function getSubscriptionsOrder(state: any) {
  const { medId, medNameId, medPriceId, quantity } = getSubscriptionsCartMed(state);
  return {
    medId,
    medNameId,
    medPriceId,
    quantity,
    sanofiConsentGiven: false,
  };
}

const initialState = {
  subscriptions: [],
  cart: null,
  subsEligibility: {},
  subscriptionLimitations: {},
  subsActionItems: null,
};
/* ------------------ reducer ------------------ */

export default function reducer(state: SubscriptionsState = initialState, action: SubscriptionAction) {
  switch (action.type) {
    case POPULATE_SUBSCRIPTION:
      return { ...state, subscriptions: action.payload };

    case GET_CART_INFO:
      return { ...state, cart: action.payload };

    case SET_SUBS_ELIGIBILITY:
      return { ...state, subsEligibility: { ...state.subsEligibility, ...action.payload } };

    case POPULATE_SUBSCRIPTION_LIMITATION:
      return {
        ...state,
        subscriptionLimitations: { ...state.subscriptionLimitations, [action.payload.medNameId]: action.payload },
      };

    case PURCHASE:
      return { ...state, subscriptions: [], subsEligibility: {} };

    case GET_SUBS_ACTION_ITEMS:
      return { ...state, subsActionItems: action.payload };

    case USER_SELECTED_MED: {
      const selectedMDVMed = action.payload;
      return { ...state, selectedMDVMed };
    }

    default:
      return state;
  }
}
